import { useQuery, useQueryClient, useMutation } from "react-query";
import { toast } from "react-toastify";
import API from "./utils/api";
import {
	CreateEmployeeWorkTimeDto,
	EmployeeWorkTimeDto,
	EmployeeWorkTimePaginator,
	QueryEmployeeWorkTimeDto,
	UpdateEmployeeWorkTimeDto,
} from "../models/employee-work-time.dto";
import ENDPOINTS from "./utils/endpoints";

export function useGetEmployeeWorkTime(options?: QueryEmployeeWorkTimeDto) {
	const url = ENDPOINTS.HR.EMPLOYEE_WORK_TIME.GET;
	const request = (options: QueryEmployeeWorkTimeDto) =>
		API.HrAPI.getEmployeeWorkTime(options);

	const { data, isLoading, error, refetch } = useQuery<
		EmployeeWorkTimePaginator,
		Error
	>(
		[url, options],
		({ queryKey, pageParam }) =>
			request(Object.assign({}, queryKey[1], pageParam)),
		{ ...API.Handlers.pageParamOption },
	);

	return { data, isLoading, error, refetch };
}

export function useGetEmployeeWorkTimeById(id: number) {
	const url = ENDPOINTS.HR.EMPLOYEE_WORK_TIME.DETAIL;
	const request = API.HrAPI.getEmployeeWorkTimeById(id);

	const { data, isLoading, error } = useQuery<
		{ data: EmployeeWorkTimeDto },
		Error
	>([url, id], () => request, { enabled: !!id });

	return { data, isLoading, error };
}

export function useCreateEmployeeWorkTime() {
	const queryClient = useQueryClient();

	return useMutation(
		({ payload }: { payload: CreateEmployeeWorkTimeDto }) =>
			API.HrAPI.createEmployeeWorkTime(payload),
		{
			onSuccess: (response) => {
				toast.success("Record created successfully");
				queryClient.invalidateQueries(ENDPOINTS.HR.EMPLOYEE_WORK_TIME.GET);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useUpdateEmployeeWorkTime() {
	const queryClient = useQueryClient();

	return useMutation(
		({ id, payload }: { id: number; payload: UpdateEmployeeWorkTimeDto }) =>
			API.HrAPI.updateEmployeeWorkTime(id, payload),
		{
			onSuccess: () => {
				toast.success("Record updated successfully");
				queryClient.invalidateQueries(ENDPOINTS.HR.EMPLOYEE_WORK_TIME.GET);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useDeleteEmployeeWorkTime() {
	return useMutation(
		({ id }: { id: number }) => API.HrAPI.deleteEmployeeWorkTime(id),
		{
			onSuccess: () => {
				toast.success("Record deleted successfully");
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useUploadEmployeeWorkTimes() {
	return useMutation(
		({ companyId, payload }: { companyId: number; payload: FormData }) =>
			API.HrAPI.uploadEmployeeWorkTimes(companyId, payload),
		{
			onSuccess: () => {},
			onError: API.Handlers.onErrorHandler,
			onSettled: () => {},
		},
	);
}
